import React from 'react';
import { MessageSquare, User, FileText, Settings, Heart, Star } from 'lucide-react';
import { Button, Card, CardContent, CardHeader, CardTitle, CardDescription, Input, Tag, Link } from '../components/ui';

/**
 * Brand Showcase Page
 * 
 * This page demonstrates the proper usage of the RENEW MYDAY brand system.
 * Use this as a reference for implementing consistent design patterns.
 */
const BrandShowcase: React.FC = () => {
  return (
    <div className="min-h-screen bg-rm-bg-0">
      {/* Hero Section - Dark Navy */}
      <section className="bg-rm-primary-700 text-white">
        <div className="mx-auto max-w-7xl px-6 py-16">
          <h1 className="text-4xl font-semibold">Brand System Showcase</h1>
          <p className="mt-4 max-w-2xl opacity-90 text-lg">
            This page demonstrates the complete RENEW MYDAY brand system including colors, 
            typography, components, and layout patterns.
          </p>
          <div className="mt-8 flex gap-4">
            <Button variant="primary">Primary Action</Button>
            <Button variant="secondary">Secondary Action</Button>
            <Button variant="ghost" className="text-white hover:bg-white/10">
              Ghost Button
            </Button>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="mx-auto max-w-7xl px-6 py-10">
        {/* Typography Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-semibold text-rm-primary-700 mb-8">Typography</h2>
          <div className="grid gap-6 md:grid-cols-2">
            <Card className="p-6">
              <CardHeader>
                <CardTitle>Headings</CardTitle>
                <CardDescription>Navy blue headings for clear hierarchy</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <h1 className="text-4xl font-semibold text-rm-primary-700">Heading 1</h1>
                  <h2 className="text-3xl font-semibold text-rm-primary-700">Heading 2</h2>
                  <h3 className="text-2xl font-semibold text-rm-primary-700">Heading 3</h3>
                  <h4 className="text-xl font-semibold text-rm-primary-700">Heading 4</h4>
                </div>
              </CardContent>
            </Card>

            <Card className="p-6">
              <CardHeader>
                <CardTitle>Body Text</CardTitle>
                <CardDescription>Charcoal and muted text for readability</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-lg text-rm-ink-900">Large body text for hero sections</p>
                  <p className="text-base text-rm-ink-900">Default body text for content</p>
                  <p className="text-sm text-rm-ink-900">Small body text for details</p>
                  <p className="text-sm text-rm-neutral-600">Muted text for secondary information</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Button Variants */}
        <section className="mb-16">
          <h2 className="text-3xl font-semibold text-rm-primary-700 mb-8">Buttons</h2>
          <Card className="p-6">
            <CardHeader>
              <CardTitle>Button Variants</CardTitle>
              <CardDescription>Gold gradient for primary actions, navy for secondary</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                <Button variant="primary">
                  <MessageSquare className="w-4 h-4" />
                  Primary Button
                </Button>
                <Button variant="secondary">
                  <User className="w-4 h-4" />
                  Secondary Button
                </Button>
                <Button variant="ghost">
                  <FileText className="w-4 h-4" />
                  Ghost Button
                </Button>
                <Button variant="outline">
                  <Settings className="w-4 h-4" />
                  Outline Button
                </Button>
                <Button variant="link">Link Button</Button>
              </div>
              
              <div className="mt-6">
                <h4 className="text-lg font-medium text-rm-primary-700 mb-3">Button Sizes</h4>
                <div className="flex flex-wrap items-center gap-4">
                  <Button variant="primary" size="sm">Small</Button>
                  <Button variant="primary" size="default">Default</Button>
                  <Button variant="primary" size="lg">Large</Button>
                  <Button variant="primary" size="icon">
                    <Heart className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Card Examples */}
        <section className="mb-16">
          <h2 className="text-3xl font-semibold text-rm-primary-700 mb-8">Cards</h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {/* Simple Card */}
            <Card className="p-6">
              <CardHeader>
                <CardTitle>Simple Card</CardTitle>
                <CardDescription>Basic card with title and description</CardDescription>
              </CardHeader>
              <CardContent>
                <p>This is a simple card example with the brand styling applied.</p>
              </CardContent>
            </Card>

            {/* Program Support Card */}
            <Card className="p-6">
              <CardHeader>
                <CardTitle>Renewmyday Today</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2 items-center">
                  <div>
                    <p className="text-sm text-rm-neutral-600">Every</p>
                    <p className="font-medium">Tuesday</p>
                  </div>
                  <div>
                    <p className="text-sm text-rm-neutral-600">Time</p>
                    <p className="font-semibold">17:00 PM</p>
                  </div>
                </div>
                <Button variant="primary" className="w-full mt-4">
                  Join Session
                </Button>
                <p className="mt-3 text-sm text-rm-neutral-600">
                  Registration is not necessary
                </p>
              </CardContent>
            </Card>

            {/* Feature Card */}
            <Card className="p-6">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Star className="w-6 h-6 text-rm-accent-500" />
                  <CardTitle>Featured Content</CardTitle>
                </div>
                <CardDescription>Card with icon and action</CardDescription>
              </CardHeader>
              <CardContent>
                <p>You may want to prepare for a conversation with a client, colleague or manager.</p>
                <div className="mt-4 flex items-center gap-3">
                  <Tag variant="coaching">Coaching</Tag>
                  <Tag variant="support">Support</Tag>
                  <Link to="#" className="ml-auto">Audio tour</Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Form Elements */}
        <section className="mb-16">
          <h2 className="text-3xl font-semibold text-rm-primary-700 mb-8">Form Elements</h2>
          <Card className="p-6">
            <CardHeader>
              <CardTitle>Form Components</CardTitle>
              <CardDescription>Inputs and form elements with brand styling</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-rm-primary-700 mb-2">
                    Your Name
                  </label>
                  <Input placeholder="Enter your name..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-rm-primary-700 mb-2">
                    Email Address
                  </label>
                  <Input type="email" placeholder="your@email.com" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-rm-primary-700 mb-2">
                    Message
                  </label>
                  <textarea 
                    className="w-full rounded-md border border-rm-neutral-600/30 bg-white px-4 py-2 text-rm-ink-900 placeholder:text-rm-neutral-600/80 focus:outline-none focus:ring-2 focus:ring-rm-accent-500"
                    rows={4}
                    placeholder="Type your message here..."
                  />
                </div>
              </div>
              <div className="mt-6">
                <Button variant="primary">Submit Form</Button>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Tags and Links */}
        <section className="mb-16">
          <h2 className="text-3xl font-semibold text-rm-primary-700 mb-8">Tags & Links</h2>
          <Card className="p-6">
            <CardHeader>
              <CardTitle>Interactive Elements</CardTitle>
              <CardDescription>Tags, links, and other interactive components</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div>
                  <h4 className="text-sm font-medium text-rm-primary-700 mb-3">Tags</h4>
                  <div className="flex flex-wrap gap-2">
                    <Tag variant="default">Default</Tag>
                    <Tag variant="support">Support</Tag>
                    <Tag variant="coaching">Coaching</Tag>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-rm-primary-700 mb-3">Links</h4>
                  <div className="space-y-2">
                    <div><Link to="#">Internal navigation link</Link></div>
                    <div><Link to="https://example.com" external>External link</Link></div>
                    <div><Link to="#" className="text-rm-teal-600 hover:text-rm-primary-700">Custom styled link</Link></div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Color Palette */}
        <section className="mb-16">
          <h2 className="text-3xl font-semibold text-rm-primary-700 mb-8">Color Palette</h2>
          <div className="grid gap-6 md:grid-cols-2">
            <Card className="p-6">
              <CardHeader>
                <CardTitle>Primary Colors</CardTitle>
                <CardDescription>Navy and gold brand colors</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-rm-primary-700 rounded-md"></div>
                    <div>
                      <p className="font-medium">Navy Primary</p>
                      <p className="text-sm text-rm-neutral-600">#233E59</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-rm-accent-500 rounded-md"></div>
                    <div>
                      <p className="font-medium">Gold Accent</p>
                      <p className="text-sm text-rm-neutral-600">#D9B059</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-rm-ink-900 rounded-md"></div>
                    <div>
                      <p className="font-medium">Charcoal Text</p>
                      <p className="text-sm text-rm-neutral-600">#2C353A</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="p-6">
              <CardHeader>
                <CardTitle>Supporting Colors</CardTitle>
                <CardDescription>Neutral and accent colors</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-rm-neutral-600 rounded-md"></div>
                    <div>
                      <p className="font-medium">Neutral</p>
                      <p className="text-sm text-rm-neutral-600">#91908E</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-rm-teal-600 rounded-md"></div>
                    <div>
                      <p className="font-medium">Teal Accent</p>
                      <p className="text-sm text-rm-neutral-600">#60746F</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-rm-bg-50 border border-rm-neutral-600/20 rounded-md"></div>
                    <div>
                      <p className="font-medium">Card Background</p>
                      <p className="text-sm text-rm-neutral-600">#EEF1F8</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-rm-primary-700 text-white">
        <div className="mx-auto max-w-7xl px-6 py-8 flex justify-between items-center">
          <p className="opacity-80">© RENEW MYDAY Brand System</p>
          <Link to="#" className="text-rm-accent-300 hover:text-rm-accent-500">
            Style Guide
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default BrandShowcase;